import {
    Box, Chip, CircularProgress, Divider, LinearProgress,
    Table, TableBody, TableCell, TableHead, TableRow, Typography
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import moment from 'moment';
import ApiServices from '../../services/ApiServices';

const TERMINAL_STAGES = [
    'OTA_COMPLETE', 'FLASH_SUCCESS', 'OTA_FAILED',
    'REQUEST_REJECTED', 'DOWNLOAD_FAILED', 'FLASH_FAILED', 'ABORT_ACCEPTED', 'ABORT_REJECTED',
];

const stageColor = (stage) => {
    if (!stage) return 'default';
    if (stage === 'OTA_COMPLETE' || stage === 'FLASH_SUCCESS') return 'success';
    if (['OTA_FAILED', 'REQUEST_REJECTED', 'DOWNLOAD_FAILED', 'FLASH_FAILED', 'ABORT_REJECTED'].includes(stage)) return 'error';
    if (stage === 'ABORT_ACCEPTED') return 'warning';
    if (stage === 'PENDING') return 'default';
    return 'info';
};

const BINARY_COLUMNS = [
    { field: 'binaryType', headerName: 'Component', flex: 0.8, sortable: false },
    { field: 'versionFrom', headerName: 'From', flex: 0.7, sortable: false,
      renderCell: (p) => p.value ?? '—' },
    { field: 'versionTo', headerName: 'To', flex: 0.7, sortable: false },
    { field: 'stage', headerName: 'Stage', flex: 0.9, sortable: false,
      renderCell: (p) => <Chip label={p.value ?? '—'} color={stageColor(p.value)} size="small" /> },
    { field: 'progressPercent', headerName: 'Progress', flex: 0.8, sortable: false,
      renderCell: (p) => {
          const pct = p.value ?? 0;
          return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <LinearProgress variant="determinate" value={pct} sx={{ flex: 1 }} />
                  <Typography variant="caption">{pct}%</Typography>
              </Box>
          );
      }
    },
    { field: 'infoMsg', headerName: 'Info', flex: 1.4, sortable: false,
      renderCell: (p) => (
          <Typography variant="caption" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.4 }}>
              {p.value ?? '—'}
          </Typography>
      )
    },
    { field: 'errorCode', headerName: 'Error', flex: 0.5, sortable: false,
      renderCell: (p) => p.value != null ? p.value : '—' },
    { field: 'updatedAt', headerName: 'Updated', flex: 1, sortable: false,
      renderCell: (p) => p.value ? moment(p.value).format('DD-MM-YYYY HH:mm:ss') : '—' },
];

function GwOtaSessionDetail({ otaId }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        ApiServices.getGwSelfOtaSession(otaId)
            .then(res => setDetail(res.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [otaId]);

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}><CircularProgress size={24} /></Box>;
    }
    if (!detail) {
        return <Typography>Session not found.</Typography>;
    }

    const { session, binaries } = detail;
    const isTerminal = TERMINAL_STAGES.includes(session.stage);
    const binaryColumns = isTerminal
        ? BINARY_COLUMNS.filter(c => c.field !== 'progressPercent')
        : BINARY_COLUMNS;

    return (
        <Box sx={{ minWidth: 640 }}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="body2">
                    <b>Stage:</b>{' '}
                    <Chip label={session.stage ?? '—'} color={stageColor(session.stage)} size="small" />
                </Typography>
                <Typography variant="body2"><b>Started by:</b> {session.updatedBy ?? '—'}</Typography>
                <Typography variant="body2"><b>Force Update:</b> {session.forceUpdate ? 'Yes' : 'No'}</Typography>
                <Typography variant="body2"><b>Retries:</b> {session.retryCount ?? 0}</Typography>
                <Typography variant="body2"><b>Started:</b>{' '}
                    {session.createdAt ? moment(session.createdAt).format('DD-MM-YYYY HH:mm:ss') : '—'}
                </Typography>
                <Typography variant="body2"><b>Updated:</b>{' '}
                    {session.updatedAt ? moment(session.updatedAt).format('DD-MM-YYYY HH:mm:ss') : '—'}
                </Typography>
            </Box>
            <DataGrid
                rows={binaries ?? []}
                columns={binaryColumns}
                getRowId={(row) => row.id}
                autoHeight
                hideFooter
                disableColumnMenu
                disableSelectionOnClick
                getRowHeight={() => 'auto'}
                getEstimatedRowHeight={() => 52}
                sx={{ '& .MuiDataGrid-cell': { py: 1 } }}
                slots={{
                    noRowsOverlay: () => (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <Typography color="text.secondary">No binary status data</Typography>
                        </Box>
                    )
                }}
            />

            {(session.stageHistory ?? []).length > 0 && (
                <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Stage History</Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Stage</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Timestamp</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(session.stageHistory ?? []).map((entry, idx) => (
                                <TableRow key={idx}>
                                    <TableCell>
                                        <Chip label={entry.stage} color={stageColor(entry.stage)} size="small" />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">
                                            {entry.ts ? moment(entry.ts).format('DD-MM-YYYY HH:mm:ss') : '—'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </>
            )}
        </Box>
    );
}

export default GwOtaSessionDetail;